import BlockContent from '~/components/atoms/BlockContent/BlockContent'
import Container from '~/components/atoms/Container/Container'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import VideoEmbed from '~/components/atoms/VideoEmbed/VideoEmbed'
import ReportCard from '~/components/blocks/ReportCard/ReportCard'
import type { PageData } from '~/components/report/types'
import useMabPostHog, {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/hooks/useMabPostHog'
import useReportSignUp from '~/hooks/useReportSignUp'

import styles from './ReportAboutSection.module.css'

type ReportAboutSectionProps = Pick<
  PageData,
  | '_id'
  | 'title'
  | 'subtitle'
  | 'summary'
  | 'leadImage'
  | 'isPdfType'
  | 'slug'
  | 'primaryCategory'
  | 'video'
  | 'introTitle'
  | 'introContent'
  | 'secondaryIntroContent'
  | 'pdfFile'
> & {
  isLoggedIn: boolean
}

export default function ReportAboutSection({
  _id,
  title,
  subtitle,
  summary,
  leadImage,
  isPdfType,
  slug,
  primaryCategory,
  video,
  introTitle,
  introContent,
  secondaryIntroContent,
  pdfFile,
  isLoggedIn,
}: ReportAboutSectionProps) {
  const { openReportSignUp } = useReportSignUp()
  const { pushEvent } = useMabPostHog()

  const handleDownload = (e: React.MouseEvent) => {
    e.preventDefault()

    if (!pdfFile?.url) {
      console.error('PDF file URL not available')
      return
    }

    const link = document.createElement('a')
    link.href = pdfFile.url
    link.target = '_blank'

    pushEvent('PDF Report Downloaded', {
      interactionLocation: PushInteractionLocation.Reports,
      interactionType: PushInteractionType.Click,
      webProduct: PushWebProduct.Reports,
    })

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const buttonClickHandler = (e: React.MouseEvent) =>
    isLoggedIn ? handleDownload(e) : openReportSignUp(leadImage)

  const hasIntroContent = introContent && introContent.length > 0
  const hasSecondaryContent =
    secondaryIntroContent && secondaryIntroContent.length > 0

  if (!hasIntroContent && !hasSecondaryContent) return null

  return (
    <section className={styles.base}>
      <Container>
        <div className={styles.columns}>
          <div className={styles.leftColumn}>
            <Text
              Tag="h2"
              size={TextSizes.primaryTitleMed}
              className={styles.sectionTitle}
            >
              About this report
            </Text>

            {video?.videoUrl && (
              <div className={styles.videoWrapper}>
                <VideoEmbed
                  videoType="youtube"
                  videoSrc={video.videoUrl}
                  watchTime={video.watchTime}
                  title={video.title}
                  hideViewOnLink
                />
              </div>
            )}

            {introTitle && (
              <Text
                Tag="h3"
                size={TextSizes.primaryTitleMed}
                className={styles.introTitle}
              >
                {introTitle || 'Why download the report?'}
              </Text>
            )}

            {introContent && introContent.length > 0 && (
              <div className={styles.introContent}>
                <BlockContent value={introContent} size={TextSizes.bodyMed} />
              </div>
            )}

            <div className={styles.insideReport}>
              <div className={styles.insideReportInner}>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M14 14L11.1 11.1M12.6667 7.33333C12.6667 10.2789 10.2789 12.6667 7.33333 12.6667C4.38781 12.6667 2 10.2789 2 7.33333C2 4.38781 4.38781 2 7.33333 2C10.2789 2 12.6667 4.38781 12.6667 7.33333Z"
                    stroke="black"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <Text
                  size={TextSizes.primaryTitleMed}
                  className={styles.insideReportLabel}
                >
                  Inside the report
                </Text>
              </div>
            </div>
            {secondaryIntroContent && secondaryIntroContent.length > 0 && (
              <ul className={styles.secondaryList}>
                {secondaryIntroContent.map((item, index) => (
                  <li key={index} className={styles.secondaryItem}>
                    {item.title && (
                      <Text
                        Tag="h4"
                        size={TextSizes.primaryTitleMed}
                        className={styles.secondaryItemTitle}
                      >
                        {item.title}
                      </Text>
                    )}
                    {item.content && item.content.length > 0 && (
                      <div className={styles.secondaryItemContent}>
                        <BlockContent
                          value={item.content}
                          size={TextSizes.bodyMed}
                        />
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={styles.rightColumn}>
            {leadImage && (
              <div className={styles.stickyCard}>
                <ReportCard
                  _id={_id}
                  title={title}
                  subtitle={subtitle ?? ''}
                  summary={summary}
                  leadImage={leadImage}
                  isPdfType={isPdfType ?? false}
                  slug={slug}
                  primaryCategory={primaryCategory}
                  variant="inlineFeatureCompact"
                  matchGridHeight={false}
                  onButtonClick={buttonClickHandler}
                />
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  )
}
